import React, { useCallback } from 'react';

// utilities
import { getDisplayTemperature } from '@fluke/irviewer/utilities';
import { getDateTimeFromMillisecondsTZ } from '../../../../../utils/getDateFormat';

// components
import { TableContainer, Paper, Table, TableHead, TableRow, TableCell } from '@mui/material';

const dateTimeHandler = (imageDateTime = {}) => {
  const { day, millisecondsSinceStartOfTheDay, month, timeZone, utcMilliseconds, year4Digits } =
    imageDateTime;

  if (utcMilliseconds === undefined) {
    const currDate = new Date(year4Digits, month - 1, day, 0);
    const currTime = new Date(0);
    currTime.setTime(millisecondsSinceStartOfTheDay);
    return new Date([currDate.toDateString(), currTime.toTimeString()]).toLocaleString(
      navigator.language,
      {
        timeZone,
        timeZoneName: 'short',
      },
    );
  } else {
    return getDateTimeFromMillisecondsTZ(utcMilliseconds, timeZone);
  }
};

export default function IRViewerInfo({ imageInfo = {}, temperatureUnit = 'C' }) {
  const {
    THERMAL_IMAGE_INFO_DATA_VGPB = {},
    THERMAL_IMAGER_INFO_DATA_GPB = {},
    CALIBRATION_DATA_VGPB = {},
  } = imageInfo ?? {};

  const {
    irSensorHeight = 0,
    irSensorWidth = 0,
    cameraSerialNumber32Chars = '',
    companyName32Chars = '',
  } = THERMAL_IMAGER_INFO_DATA_GPB;

  const { imageDateTime = {} } = THERMAL_IMAGE_INFO_DATA_VGPB;

  const { calRangesInfo } = CALIBRATION_DATA_VGPB;

  const {
    // calRangeMinTempC = 0,
    // calRangeMaxTempC = 0,
    minDisplayableTempC = 0,
    maxDisplayableTempC = 0,
  } = calRangesInfo?.[0] || {};

  const {
    capturedBackgroundTempC = 0,
    capturedEmissivity = 1.0,
    capturedTransmissionFactor = 1.0,
  } = THERMAL_IMAGE_INFO_DATA_VGPB;

  const handleDisplayTemperature = useCallback(
    (tempCelsius = 0) => {
      return getDisplayTemperature(tempCelsius, temperatureUnit);
    },
    [temperatureUnit],
  );

  const getDisplayTemperatureRange = (min, max) => {
    const minDisplay = handleDisplayTemperature(min);
    const maxDisplay = handleDisplayTemperature(max);

    return `${minDisplay} to ${maxDisplay}`;
  };

  const labelList = [
    {
      id: 'dateTime',
      label: 'Date & Time',
      value: dateTimeHandler(imageDateTime),
    },
    {
      id: 'irSensorSize',
      label: 'IR Sensor Size',
      value: `${irSensorWidth} X ${irSensorHeight}`,
    },
    {
      id: 'capturedBackgroundTempC',
      label: 'Background Temperature',
      value: handleDisplayTemperature(capturedBackgroundTempC),
    },
    {
      id: 'capturedEmissivity',
      label: 'Emissivity',
      value: `${capturedEmissivity * 100}%`,
    },
    {
      id: 'capturedTransmissionFactor',
      label: 'Transmission',
      value: `${capturedTransmissionFactor * 100}%`,
    },
    {
      id: 'calibrationRange',
      label: 'Calibration Range',
      value: getDisplayTemperatureRange(minDisplayableTempC, maxDisplayableTempC),
    },
    {
      id: 'cameraSerialNumber32Chars',
      label: 'Camera Serial',
      value: cameraSerialNumber32Chars,
    },
    {
      id: 'cameraModel',
      label: 'Camera Model',
      value: cameraSerialNumber32Chars.split('-')[0],
    },
    {
      id: 'companyName32Chars',
      label: 'Camera Manufacturer',
      value: companyName32Chars,
    },
    {
      id: null,
      label: 'Severity',
      value: '',
    },
  ];

  return (
    <div className="irViewerInfoTableParent">
      <TableContainer component={Paper}>
        <Table sx={{ minWidth: 640 }} size="small">
          <TableHead>
            {labelList.map(({ label, value }, index) => (
              <TableRow key={`info-row-${index}-${label}`}>
                <TableCell align="left">{label}</TableCell>
                <TableCell align="right">{`${value}`}</TableCell>
              </TableRow>
            ))}
          </TableHead>
        </Table>
      </TableContainer>
    </div>
  );
}
